import { FC, useState } from 'react';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import {
  AsyncTypeahead,
  ClearButton,
  Typeahead,
} from 'react-bootstrap-typeahead';
import { Option } from 'react-bootstrap-typeahead/types/types';

import { getSocietiesRegistered } from 'modules/society/redux/societyActions';
import {
  PartnerBasic,
  useGetPartnersFromAdministrated,
} from 'modules/partners/hooks/useGetPartnersFromAdministrated';

type PartnerTypeaheadProps = {
  naturalPerson: boolean;
  defaultValue?: string;
  onChangeName: (value: string) => void; // eslint-disable-line
  onChangeCif: (value: string) => void; // eslint-disable-line
  onChangeEmail: (value: string) => void; // eslint-disable-line
  placeholder?: string;
  lgSize?: boolean;
};

type ClearOptionProps = {
  onClear: () => void;
  selected: Option[];
};

type CustomOption = {
  customOption: boolean;
  label: string;
  id: string;
};

const PartnerTypeahead: FC<PartnerTypeaheadProps> = ({
  naturalPerson,
  defaultValue,
  onChangeName,
  onChangeCif,
  onChangeEmail,
  placeholder = '',
  lgSize = true,
}: PartnerTypeaheadProps) => {
  const { t } = useTranslate();

  const user = useSelector((state: State) => state?.user);

  const { partnersFromAdministrated } = useGetPartnersFromAdministrated(
    user._id
  );

  const [naturalPersonOptions, setNaturalPersonOptions] = useState<
    PartnerBasic[]
  >([]);
  const [juridicPersonOptions, setJuridicPersonOptions] = useState<string[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [textFromSociety, setTextFromSociety] = useState<string>('');

  const clearSelectedOption = () => {
    onChangeName('');
    onChangeCif('');
    onChangeEmail('');
  };

  const handleChangeNaturalPersonOption = (selectedOption: Option[]) => {
    if (selectedOption.length > 0) {
      const option = selectedOption[0] as Record<string, string>;
      onChangeName(option?.name);
      onChangeCif(option?.cif);
      onChangeEmail(option?.email);
    } else {
      clearSelectedOption();
    }
  };

  const handleChangeJuridicPersonOption = (
    selectedOption: Option[] | CustomOption
  ) => {
    if (!selectedOption) {
      clearSelectedOption();
      return;
    }

    if ('customOption' in selectedOption && selectedOption.customOption) {
      onChangeName(selectedOption.label);
    } else if (Array.isArray(selectedOption) && selectedOption.length > 0) {
      const option = selectedOption[0] as Record<string, string>;
      const name = option.nameAddress || '';
      const registeredOfficeCode = option.registeredOffice || '';

      onChangeName(name);
      onChangeCif(registeredOfficeCode);
    }

    setJuridicPersonOptions([]);
    setTextFromSociety('');
  };

  const handleAutoCompleteBlur = () => {
    if (textFromSociety) {
      handleChangeJuridicPersonOption({
        customOption: true,
        label: textFromSociety,
        id: 'new-id-1',
      });
    }
  };

  const handleClearOptions = ({ onClear, selected }: ClearOptionProps) => (
    <div className="rbt-aux">
      {!!selected.length && <ClearButton onClick={onClear} />}
    </div>
  );

  const handleSearchJuridicPerson = (query: string) => {
    onChangeName(query);
    setIsLoading(true);

    setTextFromSociety(query);
    getSocietiesRegistered(query).then((societies) => {
      setJuridicPersonOptions(societies);
    });
    setIsLoading(false);
  };

  const handleSearchNaturalPerson = (query: string) => {
    onChangeName(query);

    const filteredOption = partnersFromAdministrated.filter((partner) =>
      partner?.name?.toLowerCase().includes(query.toLowerCase())
    );
    setNaturalPersonOptions(filteredOption);
  };

  return (
    <>
      <div className="form-control-wrap">
        <div className="form-icon form-icon-left">
          <em className="icon ni ni-user" />
        </div>
        {naturalPerson ? (
          <Typeahead
            id="natural-person-search-box"
            className="form-input-icon-left"
            labelKey="name"
            minLength={1}
            defaultInputValue={defaultValue}
            onInputChange={handleSearchNaturalPerson}
            options={naturalPersonOptions}
            newSelectionPrefix={`${t('Select:')} `}
            placeholder={placeholder}
            onChange={handleChangeNaturalPersonOption}
            size={lgSize ? 'lg' : 'sm'}
          >
            {handleClearOptions}
          </Typeahead>
        ) : (
          <AsyncTypeahead
            id="juridic-person-search-box"
            className="form-input-icon-left"
            isLoading={isLoading}
            labelKey="label"
            minLength={3}
            onChange={handleChangeJuridicPersonOption}
            onBlur={handleAutoCompleteBlur}
            onSearch={handleSearchJuridicPerson}
            options={juridicPersonOptions}
            useCache={false}
            newSelectionPrefix={`${t('Select:')} `}
            placeholder={placeholder}
            size={lgSize ? 'lg' : 'sm'}
          >
            {handleClearOptions}
          </AsyncTypeahead>
        )}
      </div>
    </>
  );
};

export default PartnerTypeahead;
